import { useCallback } from 'react'
import type { DataNode } from '../types'
import { useContentItemViewNode } from './useContentItemViewNodeService'
import { useArticleCollapse } from '../features/scrollToNewContent/hooks/useArticleCollapse'

/**
 * Custom hook to handle actions related to Article content items
 * @returns Actions related to Article content items
 */
export const useArticleActions = () => {
    const {
        findContentItemNode,
        insertNodeAfterSibling,
        setAddingNode,
        createContentItemViewNode,
    } = useContentItemViewNode()
    
    const { collapseParentArticle } = useArticleCollapse()

    /**
     * Adds an Article content item after a sibling content item
     * @param sibling Sibling content item to insert this node after
     */
    const addArticleAfterSibling = useCallback(
        (sibling: DataNode) => {
            const siblingNode = findContentItemNode(sibling.key)

            if (!siblingNode) return

            // Save new node and reorder siblings to Local State
            const articleNode = createContentItemViewNode('', siblingNode)
            insertNodeAfterSibling(articleNode, siblingNode.id)
            
            // Collapse parent article using the abstracted hook
            collapseParentArticle(sibling)
            
            // Set currently being added node to focus
            setAddingNode(articleNode)
        },
        [
            findContentItemNode,
            createContentItemViewNode,
            insertNodeAfterSibling,
            collapseParentArticle,
            setAddingNode,
        ]
    )

    return { addArticleAfterSibling }
}
